import React from 'react';
import {
  ProfileExperienceCard,
  ProfileTab,
  ProfileTabOption,
} from '../constants';
import styles from '../Profile.module.css';

const servicesExperienceIconStyle = {
  '--profile-information-icon-url': `url("${process.env.PUBLIC_URL}/icons/service-fill.svg")`,
} as React.CSSProperties;

const serviceTabPresentation: Record<ProfileTab, {
  icon: string;
  emptyDescription: string;
  emptyActionLabel: string;
}> = {
  athletic: {
    icon: 'running-shoes.svg',
    emptyDescription: 'Showcase your background, achievements, and services to stand out.',
    emptyActionLabel: 'Add Experience',
  },
  artistic: {
    icon: 'art-fill.svg',
    emptyDescription: 'Showcase your creative work, skills, and experiences to stand out.',
    emptyActionLabel: 'Add Experience',
  },
  services: {
    icon: 'general-service.svg',
    emptyDescription: 'Share the services you offer so people know how you can help.',
    emptyActionLabel: 'Add Service',
  },
  college: {
    icon: 'university-solid.svg',
    emptyDescription: 'Highlight college or university opportunities available to your audience.',
    emptyActionLabel: 'Add Opportunity',
  },
};

const serviceTabIconStyle = (fileName: string) => ({
  '--profile-service-tab-icon-url': `url("${process.env.PUBLIC_URL}/icons/${fileName}")`,
}) as React.CSSProperties;

const editActionIconStyle = {
  '--profile-information-icon-url': `url("${process.env.PUBLIC_URL}/icons/edit-filled.svg")`,
} as React.CSSProperties;

const serviceActionIconStyle = (fileName: string) => ({
  '--profile-service-action-icon-url': `url("${process.env.PUBLIC_URL}/icons/${fileName}")`,
}) as React.CSSProperties;

const serviceDetailIconFileNames: Record<string, string> = {
  Level: 'level-outline.svg',
  Experience: 'experience-outline.svg',
  Active: 'calendar-bold.svg',
  Availability: 'schedule-outline.svg',
  Certifications: 'certificate-success.svg',
  Description: 'post-fill.svg',
  Rate: 'quick-stats.svg',
  Contact: 'message-fill.svg',
  Portfolio: 'worldwide-web-sync.svg',
  Opportunity: 'university-solid.svg',
  Eligibility: 'user-group.svg',
  Deadline: 'calendar-bold.svg',
  'Learn More': 'worldwide-web-sync.svg',
};

const serviceDetailIconStyle = (label: string) => ({
  '--profile-service-detail-icon-url': `url("${process.env.PUBLIC_URL}/icons/${
    serviceDetailIconFileNames[label] || 'category-thin.svg'
  }")`,
}) as React.CSSProperties;

interface ProfileServicesSectionProps {
  activeTab: ProfileTab;
  tabOptions: ProfileTabOption[];
  cardsByTab: Record<ProfileTab, ProfileExperienceCard[]>;
  managerError: string | null;
  actionsDisabled: boolean;
  isEditable?: boolean;
  title?: string;
  onAddItem?: () => void;
  onEditItem?: (id: string) => void;
  onDeleteItem?: (id: string, title: string) => void;
  onTabChange: (tab: ProfileTab) => void;
}

const ProfileServicesSection: React.FC<ProfileServicesSectionProps> = ({
  activeTab,
  tabOptions,
  cardsByTab,
  managerError,
  actionsDisabled,
  isEditable = true,
  title = 'My Services & Experiences',
  onAddItem,
  onEditItem,
  onDeleteItem,
  onTabChange,
}) => {
  const activeCards = cardsByTab[activeTab];
  const emptyMessageByTab: Record<ProfileTab, string> = {
    athletic: 'No athletic experiences added yet.',
    artistic: 'No artistic experiences added yet.',
    services: 'No general services added yet.',
    college: 'No college or university opportunities added yet.',
  };
  const activeTabPresentation = serviceTabPresentation[activeTab];
  const activeTabId = `profile-services-tab-${activeTab}`;
  const activePanelId = `profile-services-panel-${activeTab}`;
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, tabIndex: number) => {
    let nextTabIndex: number | null = null;

    if (event.key === 'ArrowRight') {
      nextTabIndex = (tabIndex + 1) % tabOptions.length;
    } else if (event.key === 'ArrowLeft') {
      nextTabIndex = (tabIndex - 1 + tabOptions.length) % tabOptions.length;
    } else if (event.key === 'Home') {
      nextTabIndex = 0;
    } else if (event.key === 'End') {
      nextTabIndex = tabOptions.length - 1;
    }

    if (nextTabIndex === null) {
      return;
    }

    event.preventDefault();
    const nextTab = tabOptions[nextTabIndex];
    const tabButtons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabButtons?.[nextTabIndex]?.focus();
    onTabChange(nextTab.id);
  };

  return (
    <section
      className={`${styles.profileSection} ${styles.profileInformationSection}`}
      aria-labelledby="profile-services-experiences-title"
    >
      <div className={styles.profileInformationHeader}>
        <div className={styles.profileInformationHeading}>
          <div className={styles.profileInformationTitleRow}>
            <span className={styles.profileInformationHeadingIconTile} aria-hidden="true">
              <span
                className={styles.profileInformationHeadingIcon}
                style={servicesExperienceIconStyle}
              />
            </span>
            <h3
              id="profile-services-experiences-title"
              className={`${styles.sectionTitle} ${styles.profileInformationTitle}`}
            >
              {title}
            </h3>
          </div>
          <p className={styles.profileInformationSubtitle}>
            {isEditable
              ? 'Manage your services, experiences, and opportunities.'
              : 'Explore services, experiences, and opportunities.'}
          </p>
        </div>
      </div>

      <div className={styles.servicesExperiencesDivider} aria-hidden="true" />

      {managerError ? (
        <div className={styles.sectionErrorMessage}>{managerError}</div>
      ) : null}

      <div className={styles.serviceWorkspaceToolbar}>
        <div className={styles.serviceTabs} role="tablist" aria-label="Services and experiences">
          {tabOptions.map((tabOption, tabIndex) => {
            const isActive = activeTab === tabOption.id;
            const tabId = `profile-services-tab-${tabOption.id}`;
            const panelId = `profile-services-panel-${tabOption.id}`;

            return (
              <button
                key={tabOption.id}
                id={tabId}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={panelId}
                tabIndex={isActive ? 0 : -1}
                className={`${styles.serviceTab} ${isActive ? styles.serviceTabActive : ''}`.trim()}
                onClick={() => onTabChange(tabOption.id)}
                onKeyDown={(event) => handleTabKeyDown(event, tabIndex)}
              >
                <span
                  className={styles.serviceTabIcon}
                  style={serviceTabIconStyle(serviceTabPresentation[tabOption.id].icon)}
                  aria-hidden="true"
                />
                <span>{tabOption.label}</span>
              </button>
            );
          })}
        </div>

      </div>

      <div
        id={activePanelId}
        role="tabpanel"
        aria-labelledby={activeTabId}
        className={styles.serviceWorkspacePanel}
      >
        {activeCards.length > 0 ? (
          <div className={styles.serviceCards}>
            {activeCards.map((card) => (
              <article key={card.id} className={styles.serviceCard}>
                <div className={styles.serviceCardHeader}>
                  <div className={styles.serviceCardIdentity}>
                    <span className={styles.serviceCardIconTile} aria-hidden="true">
                      <span
                        className={styles.serviceCardIcon}
                        style={serviceTabIconStyle(activeTabPresentation.icon)}
                      />
                    </span>
                    <h4 className={styles.serviceCardTitle}>{card.title}</h4>
                  </div>
                  {isEditable && onEditItem && onDeleteItem ? (
                    <div className={styles.serviceCardActions}>
                      <button
                        type="button"
                        className={styles.serviceCardEditButton}
                        onClick={() => onEditItem(card.id)}
                        disabled={actionsDisabled}
                        aria-label={`Edit ${card.title}`}
                      >
                        <span
                          className={styles.serviceCardActionIcon}
                          style={serviceActionIconStyle('edit-filled.svg')}
                          aria-hidden="true"
                        />
                        Edit
                      </button>
                      <button
                        type="button"
                        className={styles.serviceCardDeleteButton}
                        onClick={() => onDeleteItem(card.id, card.title)}
                        disabled={actionsDisabled}
                        aria-label={`Delete ${card.title}`}
                      >
                        <span
                          className={styles.serviceCardActionIcon}
                          style={serviceActionIconStyle('baseline-delete.svg')}
                          aria-hidden="true"
                        />
                        Delete
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className={styles.serviceCardDetails}>
                  {card.rows.map((row) => (
                    <div
                      key={`${card.id}-${row.label}`}
                      className={`${styles.serviceCardRow} ${
                        row.accent || row.label === 'Level' ? styles.serviceCardRowAccent : ''
                      }`.trim()}
                    >
                      <span className={styles.serviceCardRowIconTile} aria-hidden="true">
                        <span
                          className={styles.serviceCardRowIcon}
                          style={serviceDetailIconStyle(row.label)}
                        />
                      </span>
                      <span className={styles.serviceCardRowCopy}>
                        <span className={styles.serviceCardRowLabel}>{row.label}</span>
                        <span className={styles.serviceCardRowValue}>{row.value}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className={styles.servicesEmptyState}>
            <span className={`${styles.servicesEmptyDots} ${styles.servicesEmptyDotsLeft}`} aria-hidden="true" />
            <span className={`${styles.servicesEmptyDots} ${styles.servicesEmptyDotsRight}`} aria-hidden="true" />
            <span className={styles.servicesEmptyIconTile} aria-hidden="true">
              <span
                className={styles.servicesEmptyIcon}
                style={serviceTabIconStyle(activeTabPresentation.icon)}
              />
            </span>
            <h4 className={styles.servicesEmptyTitle}>{emptyMessageByTab[activeTab]}</h4>
            <p className={styles.servicesEmptyDescription}>{activeTabPresentation.emptyDescription}</p>
            {isEditable && onAddItem ? (
              <button
                type="button"
                className={styles.servicesEmptyAction}
                onClick={onAddItem}
                disabled={actionsDisabled}
              >
                <span
                  className={styles.profileInformationEditIcon}
                  style={editActionIconStyle}
                  aria-hidden="true"
                />
                {activeTabPresentation.emptyActionLabel}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

export default React.memo(ProfileServicesSection);
